<template>
  <div :class="{'wk-field-has-required-indicator': required}">
    <label class="wkce-checkbox wkce-checkbox--color-primary
             wkce-checkbox--box-position-left wk-field-label"
      :class="[{'is-checked': checked},
               {'is-disabled': disabled},
               {'is-active': isActive}]">
      <div class="wkce-checkbox__checkmark-container">
        <input type="checkbox"
               class="wkce-checkbox__input"
               v-bind="attrs"
               @click="onClick"
               @keypress="onKeypress"
               @focus="onFocus"
               @blur="onBlur"
               @change="onChange">
        <div class="wkce-checkbox__checkmark">
          <div class="wkce-checkbox__checkmark-background" />
          <div class="wkce-checkbox__focus-ring" />
        </div>
      </div>
      <div class="wkce-checkbox__label-text">
        <slot />
      </div>
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useAttrs } from 'vue';

const attrs = useAttrs();
const emit = defineEmits([ 'checkbox-changed' ]);

defineProps({
    required: {
        type: Boolean,
        default: false
    }
});

const isActive = ref(false);
const checked = ref(Boolean(attrs.checked));
const disabled = ref(attrs.disabled);

const onClick = () => {
  checked.value = !checked.value;
}

const onKeypress = (e: KeyboardEvent) => {
  if (e.key !== 'Enter') {
    return;
  }
  checked.value = !checked.value;
  e.preventDefault();
  emit('checkbox-changed', checked.value);
};

const onFocus = () => {
    isActive.value = true;
};

const onBlur = () => {
    isActive.value = false;
};

const onChange = () => {
    emit('checkbox-changed', checked.value);
};
</script>

<style scoped lang="less">
@import "~_acaAssets/global/variables";

.wkce-checkbox {
  position: relative;
  display: block;
  margin: 0;
}

.wkce-checkbox__input {
  position: absolute;
  opacity: 0;
}

.wkce-checkbox__checkmark-container {
  display: table-cell;
}

.wkce-checkbox__checkmark {
  background-color: @WK_UTD_WHITE_100;
  cursor: pointer;
  position: relative;
  width: 16px;
  height: 16px;
  display: block;
  .ds1-mr-1(); /* stylelint-disable-line order/order */
  top: 2px;

  &::after {
    height: 7px;
    width: 3px;
    position: absolute;
    display: block;
    left: 6px;
    content: '';
    bottom: 3px;
    transform: rotate(45deg);

    .is-checked & {
      border-bottom: 0.125rem solid @WKCE-WHITE;
      border-right: 0.125rem solid @WKCE-WHITE;
      opacity: 1;
    }
  }

  .is-disabled & {
    cursor: default;
  }
}

.wkce-checkbox__checkmark-background {
  border: 1px solid @WK_UTD_GRAY_75;
  height: 100%;
  width: 100%;

  .is-checked & {
    background-color: @WK_UTD_BLUE_HOVER;
    border-color: @WK_UTD_BLUE_HOVER;

    .xAIMobile & {
      background-color: @WKCE-CREAM-SHADE1;
      border-color: @WKCE-CREAM-SHADE1;
    }
  }

  .is-disabled & {
    background-color: @WK_UTD_GRAY_15;
    border-color: @WK_UTD_GRAY_50;
  }

  .is-active & {
    outline: 2px solid @WK_TOPIC_FONT_COLOR;
  }
}

.wkce-checkbox__label-text {
  cursor: pointer;
  display: table-cell;

  .is-disabled & {
    color: @WK_UTD_GRAY_50;
    cursor: default;
  }
}

</style>